import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GripVertical } from 'lucide-react';

interface ResizableSliderProps {
  children: [React.ReactNode, React.ReactNode];
  initialLeftWidth?: number; // percentage (0-100)
  minLeftWidth?: number; // percentage
  maxLeftWidth?: number; // percentage
  className?: string;
}

const ResizableSlider: React.FC<ResizableSliderProps> = ({
  children,
  initialLeftWidth = 50,
  minLeftWidth = 20,
  maxLeftWidth = 80,
  className = '',
}) => {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      
      // Clamp the width between min and max values
      const clampedWidth = Math.min(Math.max(newLeftWidth, minLeftWidth), maxLeftWidth);
      setLeftWidth(clampedWidth);
    },
    [isDragging, minLeftWidth, maxLeftWidth]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.classList.add('resizing');

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.classList.remove('resizing');
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const rightWidth = 100 - leftWidth;

  return (
    <div 
      ref={containerRef}
      className={`resizable-slider-container ${className}`}
    >
      {/* Left Panel */}
      <div 
        style={{ 
          width: `${leftWidth}%`,
          minWidth: `${minLeftWidth}%`,
          maxWidth: `${maxLeftWidth}%`,
        }}
        className="resizable-slider-panel"
      >
        {children[0]}
      </div>

      {/* Draggable Slider */}
      <div
        ref={sliderRef}
        className={`resizable-slider-divider ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
      >
        <GripVertical 
          size={16} 
          className="grip-icon"
        />
      </div>

      {/* Right Panel */}
      <div 
        style={{ 
          width: `${rightWidth}%`,
        }}
        className="resizable-slider-panel"
      >
        {children[1]}
      </div>
    </div>
  );
};

export default ResizableSlider;